import {
  LGraphNode,
  LGraphCanvas,
  // type CanvasPointerEvent,
  LGraph,
  LGraphEventMode,
  type CanvasPointerEvent
} from '@/lib/litegraph/src/litegraph'
import { forEachNode } from '@/utils/graphTraversalUtil'
import type { IBaseWidget } from '@/lib/litegraph/src/types/widgets'
import type { ComfyApp } from '@/types'
import type { CanvasMouseEvent } from '@/types/custom'
/**
 * 导出 ComfyUI 工作流为 PNG 图像（可选嵌入 workflow JSON 到 tEXt chunk）
 *
 * 特性：
 * - 纯函数调用，无需实例化
 * - 自动恢复画布状态，确保 UI 不受影响
 * - 使用 try...finally 防止异常导致状态错乱
 * - 无内存泄漏：所有变量局部作用域，无闭包持有大对象
 * - 符合 PNG 规范嵌入自定义文本块
 *
 * @param app - ComfyUI 全局 app 对象（必须）
 * @param includeWorkflow - 是否将序列化的 workflow 嵌入 PNG，默认 true
 * @returns Promise<Blob> 返回 image/png 格式的 Blob
 */

export async function exportPngWorkflow(
  app: ComfyApp,
  includeWorkflow = true
): Promise<Blob> {
  // --- 内部工具函数 ---
  const n2b = (n: number): Uint8Array => {
    return new Uint8Array([
      (n >> 24) & 0xff,
      (n >> 16) & 0xff,
      (n >> 8) & 0xff,
      n & 0xff
    ])
  }

  const joinArrayBuffer = (...bufs: Array<ArrayBuffer | Uint8Array>) => {
    let totalSize = 0
    for (const buf of bufs) {
      totalSize += buf.byteLength
    }
    const result = new Uint8Array(totalSize)
    let offset = 0
    for (const buf of bufs) {
      result.set(buf instanceof Uint8Array ? buf : new Uint8Array(buf), offset)
      offset += buf.byteLength
    }
    return result
  }

  // CRC32 表（每次调用重建，安全且封装；若需优化可提至模块顶层）
  const crcTable = (() => {
    const table = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
      }
      table[n] = c
    }
    return table
  })()

  const crc32 = (data: Uint8Array): number => {
    let crc = -1
    for (let i = 0; i < data.length; i++) {
      crc = (crc >>> 8) ^ crcTable[(crc ^ data[i]) & 0xff]
    }
    return (crc ^ -1) >>> 0
  }

  // --- 计算工作流边界 ---
  const nodes = app.rootGraph._nodes
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity

  if (nodes.length === 0) {
    minX = minY = 0
    maxX = 800
    maxY = 600
  } else {
    for (const node of nodes) {
      const [x, y] = node.pos
      const bounds = node.getBounding() // [0, 0, width, height]
      const right = x + bounds[2]
      const bottom = y + bounds[3]
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (right > maxX) maxX = right
      if (bottom > maxY) maxY = bottom
    }
  }

  // 添加边距
  minX -= 100
  minY -= 100
  maxX += 100
  maxY += 100
  const bounds: [number, number, number, number] = [minX, minY, maxX, maxY]

  // --- 获取上下文并保存原始状态 ---
  const canvas = app.canvas
  const ctx = canvas.canvas.getContext('2d')!
  const originalState = {
    scale: canvas.ds.scale,
    width: canvas.canvas.width,
    height: canvas.canvas.height,
    offset: [canvas.ds.offset[0], canvas.ds.offset[1]] as [number, number],
    transform: ctx.getTransform()
  }

  try {
    // --- 调整画布以完整渲染工作流 ---
    const devicePixelRatio = window.devicePixelRatio || 1
    const w = (bounds[2] - bounds[0]) * devicePixelRatio
    const h = (bounds[3] - bounds[1]) * devicePixelRatio

    canvas.ds.scale = 1
    canvas.canvas.width = w
    canvas.canvas.height = h
    canvas.ds.offset[0] = -bounds[0]
    canvas.ds.offset[1] = -bounds[1]
    ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)

    canvas.draw(true, true)

    // --- 生成图像 Blob ---
    const workflowJson = includeWorkflow
      ? JSON.stringify(app.rootGraph.serialize())
      : null

    return await new Promise<Blob>((resolve, reject) => {
      app.canvasEl.toBlob(async (baseBlob) => {
        if (!baseBlob) {
          reject(new Error('Failed to generate base image blob'))
          return
        }

        if (!workflowJson) {
          resolve(baseBlob)
          return
        }

        try {
          const buffer = await baseBlob.arrayBuffer()
          const typedArr = new Uint8Array(buffer)

          // 构造 tEXt chunk: "tEXt" + "workflow\0<json>"
          const encoder = new TextEncoder()
          const textData = encoder.encode(`tEXtworkflow\0${workflowJson}`)
          const chunk = joinArrayBuffer(
            n2b(textData.length - 4),
            textData,
            n2b(crc32(textData))
          )

          // 在 IEND 块前插入（IEND 占最后 12 字节）
          const len = typedArr.length
          const iendStart = len - 12
          const finalBytes = joinArrayBuffer(
            typedArr.subarray(0, iendStart),
            chunk,
            typedArr.subarray(iendStart)
          )

          resolve(new Blob([finalBytes], { type: 'image/png' }))
        } catch (err) {
          reject(err)
        }
      }, 'image/png')
    })
  } finally {
    // --- 恢复原始状态 ---
    canvas.ds.scale = originalState.scale
    canvas.canvas.width = originalState.width
    canvas.canvas.height = originalState.height
    canvas.ds.offset[0] = originalState.offset[0]
    canvas.ds.offset[1] = originalState.offset[1]
    ctx.setTransform(originalState.transform)
    canvas.draw(true, true)
  }
}

export function groupByField<T, K extends keyof T>(
  arr: T[] | null | undefined,
  field: K
): Record<string, T[]> {
  return (arr || []).reduce((result: Record<string, T[]>, currentValue: T) => {
    // Convert the field value to string to use as object key
    const key = String(currentValue[field])
    if (!result[key]) {
      result[key] = []
    }
    result[key].push(currentValue)
    return result
  }, {})
}

export const customModelSelectNameList = [
  'lora_name',
  'ckpt_name',
  'hypernetwork_name',
  'unet_name',
  'sdxl_model'
]
export const customLoraModelNameRegex =
  /(lora_?\d+_name$)|(lora_?\d*$)|(lora_name)/i
// 模型选择排除弹窗的节点，使用原有的下拉框
export const excludeCustomModelSelectNodeTypes = [
  'CheckpointLoaderNF4',
  'UnetLoaderGGUF',
  'UnetLoaderGGUFAdvanced',
  'AnimateDiffLoraLoader',
  'LTXVLoader',
  'MZ_KolorsUNETLoader',
  'EasyControlLoadLora',
  'EasyControlLoadMultiLora',
  'ACELoRALoader',
  'DiffuEraserLoader'
]
// LGraph.modelLoaderTypeList = ['LoraLoader', 'CheckpointLoaderSimple', 'HypernetworkLoader', 'LoraLoaderModelOnly', 'ImageOnlyCheckpointLoader'];
export const modelLoaderExcludeTypeList = ['RIFE VFI']

export const getCurrentClickWidget = (
  node: any,
  pos: any,
  _event: any,
  LGraphCanvasThis: any
) => {
  if (
    !node.widgets ||
    !node.widgets.length ||
    (!LGraphCanvasThis.allow_interaction && !node.flags.allow_interaction)
  ) {
    return null
  }
  const x = pos[0] - node.pos[0]
  const y = pos[1] - node.pos[1]
  const width = node.size[0]
  for (let i = 0; i < node.widgets.length; ++i) {
    const w = node.widgets[i]
    if (!w || w.disabled) continue
    const widget_height = w.computeSize ? w.computeSize(width)[1] : 20
    const widget_width = w.width || width
    if (
      x < 6 ||
      x > widget_width - 12 ||
      y < w.last_y ||
      y > w.last_y + widget_height ||
      w.last_y === undefined
    )
      continue

    return w
  }
  return null
}

export const isCustomModelWidget = (widget: any) => {
  return (
    customModelSelectNameList.some(
      (name) => (widget?.name || '').indexOf(name) > -1
    ) || customLoraModelNameRegex.test(widget?.name)
  )
}

export const isExcludedModelSelectNode = (node: any) => {
  return (
    node?.type?.indexOf('VFI') > -1 ||
    /Power\sLora\sLoader\s\(rgthree\)/.test(node.type) ||
    node?.type?.search(/preprocessor/i) > -1 ||
    excludeCustomModelSelectNodeTypes.includes(node?.type)
  )
}

export const isCustomLoraName = (widget: any) => {
  const res = customLoraModelNameRegex.test(widget?.name)
  return res
}

interface ModelMatchRule {
  type: string
  reg: RegExp
  nodeList?: string[]
}

const USE_CUSTOM_SELECT_MODELS: ModelMatchRule[] = [
  { type: 'LORA', reg: /lora/gim },
  {
    type: 'CKPT',
    reg: /ckpt|^base_model|^refiner_model|^modelname$|^sd1_5_pruned$|^sd1_5_inpaint$/gim
  },
  {
    type: 'UNET',
    reg: /^(unet_|model_|ckpt|model|bg_unet|fo_unet)/gim,
    nodeList: [
      'UNETLoader',
      'MZ_CogVideoXLoader',
      'LoadAndApplyICLightUnet',
      'FluxModCheckpointLoader',
      'VisualCloze_Aplly',
      'WanVideoModelLoader',
      'FluxGuffInpainting',
      'pre_ic_light_sd15',
      'UNETLoader_Any'
    ]
  },
  {
    type: 'GGUF',
    reg: /^(unet_|gguf)/gim,
    nodeList: [
      'UnetLoaderGGUF',
      'UnetLoaderGGUFAdvanced',
      'DyPE_Model',
      'LoaderGGUF',
      'GGUFLoaderKJ'
    ]
  }
]

const EXCLUDED_WIDGETS: string[] = []

export const findMatchingWidget = (curWidget: any, node: any) => {
  const nodeName = node.type

  if (!curWidget?.name) return null

  if (EXCLUDED_WIDGETS.indexOf(curWidget.name) !== -1) return null

  const nodeListRules = USE_CUSTOM_SELECT_MODELS.filter((rule) => rule.nodeList)
  const nodeListMatch = nodeListRules.find((rule) => {
    rule.reg.lastIndex = 0
    return (
      rule.nodeList?.indexOf(nodeName) !== -1 && rule.reg.test(curWidget.name)
    )
  })

  if (nodeListMatch) {
    return {
      widget: curWidget,
      type: nodeListMatch.type
    }
  }

  // 第二步：匹配不包含nodeList的规则（遍历检查）
  const nonNodeListRules = USE_CUSTOM_SELECT_MODELS.filter(
    (rule) => !rule.nodeList
  )
  for (const rule of nonNodeListRules) {
    rule.reg.lastIndex = 0
    // 正则匹配组件名 → 匹配成功则返回结果
    if (rule.reg.test(curWidget.name)) {
      return {
        widget: curWidget,
        type: rule.type
      }
    }
  }

  return null
}

/**
 * 根据 combo 的选项值来判断具体的文件类型
 */
export const getWidgetFieldType = (
  widget: IBaseWidget,
  inputType: string,
  node: LGraphNode
): string => {
  // console.log('getComboSpecificType', widget, widget.name, node)
  // 如果不是 combo 类型，直接返回 inputType
  if (widget.type !== 'combo') return inputType

  // TODO: 上线弹窗选择模型时需使用该方法区分具体的模型类型
  const matchingWidget = findMatchingWidget(widget, node)
  if (matchingWidget) {
    return matchingWidget.type
  }

  // 获取 combo 的选项值
  const values = widget.options?.values || []

  // 根据选项值内容判断具体的文件类型
  if (Array.isArray(values) && values.length > 0) {
    const sampleValue = String(values[0] || '').toLowerCase()

    // 图像相关
    if (
      sampleValue.includes('.png') ||
      sampleValue.includes('.jpg') ||
      sampleValue.includes('.webp')
    ) {
      return 'IMAGE'
    }

    // 视频相关
    if (
      sampleValue.includes('.mp4') ||
      sampleValue.includes('.webm') ||
      sampleValue.includes('.mov')
    ) {
      return 'VIDEO'
    }

    // 音频相关
    if (
      sampleValue.includes('.mp3') ||
      sampleValue.includes('.wav') ||
      sampleValue.includes('.flac') ||
      sampleValue.includes('.ogg')
    ) {
      return 'AUDIO'
    }
  }

  return 'LIST'
}

export function findIsolatedNodes(graph: LGraph): LGraphNode[] {
  const isolatedNodes: LGraphNode[] = []

  forEachNode(graph, (node) => {
    // 跳过虚拟节点（注释等）
    if (node.isVirtualNode) return

    // 检查输入连接
    const hasInputs = node.inputs?.some((input) => input.link != null) ?? false

    // 检查输出连接
    const hasOutputs =
      node.outputs?.some((output) => output.links?.length) ?? false

    // 完全无连接的节点
    if (!hasInputs && !hasOutputs) {
      isolatedNodes.push(node)
    }
  })

  return isolatedNodes
}

export function isExportableNode(
  node: LGraphNode,
  isolatedNodes: LGraphNode[]
): boolean {
  const isOutputNode = node.constructor?.nodeData?.output_node === true
  const hasOutputs = node.constructor?.nodeData?.output?.length
    ? node.constructor.nodeData.output.length > 0
    : false
  if (node.isVirtualNode) return false
  if (isOutputNode && !hasOutputs) return false
  if (
    node.mode === LGraphEventMode.BYPASS ||
    node.mode === LGraphEventMode.NEVER
  )
    return false
  if (!node.widgets) return false
  if (isolatedNodes.includes(node)) return false
  return true
}

/**
 * 增加runnode社区模型点击弹窗处理
 */
export const handleRunnodeModelSelector = (
  e: CanvasMouseEvent,
  targetThis: LGraphCanvas
) => {
  if (!targetThis.graph) {
    return
  }

  // add custom
  let clientX_rel = e.clientX
  let clientY_rel = e.clientY

  if (targetThis.canvas) {
    const b = targetThis.canvas.getBoundingClientRect()
    clientX_rel -= b.left
    clientY_rel -= b.top
  }

  const canvasX = clientX_rel / targetThis.ds.scale - targetThis.ds.offset[0]
  const canvasY = clientY_rel / targetThis.ds.scale - targetThis.ds.offset[1]

  let x = e.clientX
  let y = e.clientY
  //console.log(y,targetThis.viewport);
  //console.log("pointerevents: processMouseDown pointerId:"+e.pointerId+" which:"+e.which+" isPrimary:"+e.isPrimary+" :: x y "+x+" "+y);
  targetThis.ds.viewport = targetThis.viewport
  let is_inside =
    !targetThis.viewport ||
    (targetThis.viewport &&
      x >= targetThis.viewport[0] &&
      x < targetThis.viewport[0] + targetThis.viewport[2] &&
      y >= targetThis.viewport[1] &&
      y < targetThis.viewport[1] + targetThis.viewport[3])

  if (!is_inside) {
    return
  }

  let node = targetThis.graph.getNodeOnPos(
    canvasX,
    canvasY,
    targetThis.visible_nodes
  )
  let skip_action = false
  // @ts-expect-error
  let is_primary = e.isPrimary === undefined || !e.isPrimary
  if (targetThis.onMouse) {
    if (targetThis.onMouse(e as unknown as CanvasPointerEvent) == true) return
  }

  //left button mouse / single finger
  if (e.which == 1 && !targetThis.pointer_is_double) {
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      return
    }

    //when clicked on top of a node
    //and it is not interactive
    if (
      node &&
      (targetThis.allow_interaction || node.flags.allow_interaction) &&
      !skip_action &&
      !targetThis.read_only
    ) {
      //it wasn't clicked on the links boxes
      if (!skip_action) {
        //widgets
        let widget = getCurrentClickWidget(
          node,
          targetThis.graph_mouse,
          e,
          targetThis
        )

        if (widget) {
          const matchingWidget = findMatchingWidget(widget, node)
          if (!matchingWidget) {
            return false
          }

          console.log('matchingWidget', matchingWidget)

          window.parent?.postMessage?.(
            {
              type: 'SelectModelRequest',
              from: 'comfy',
              data: JSON.stringify({
                type: matchingWidget.type
              }),
              nodeInfo: JSON.stringify({
                id: node.id,
                type: node.type,
                title: node.title,
                widgetName: widget.name,
                widgets_values: node.widgets_values
              })
            },
            '*'
          )

          // add custom add model click effect start
          // 因为这会return，不会再执行LGraphCanvas的processMouseDown，所以这里需要把点击的副作用状态修改一下
          if (targetThis.set_canvas_dirty_on_mouse_event)
            targetThis.dirty_canvas = true

          targetThis.mouse[0] = e.clientX
          targetThis.mouse[1] = e.clientY
          targetThis.graph_mouse[0] = canvasX
          targetThis.graph_mouse[1] = canvasY
          targetThis.last_click_position = [
            targetThis.mouse[0],
            targetThis.mouse[1]
          ]
          LGraphCanvas.active_canvas = targetThis

          // pointer_is_double 现在只读
          // if (targetThis.pointer_is_down && is_primary) {
          //   targetThis.pointer_is_double = true
          //   //console.log("pointerevents: pointer_is_double start");
          // } else {
          //   targetThis.pointer_is_double = false
          // }
          // targetThis.pointer_is_down = true

          targetThis.canvas.focus()
          // add custom add model click effect end

          return true
        }
      }
    }
  }
  return false
}
